<template>
    <b-card>
        <b-card-title>
            {{ $t('Bulk Vimeo ID Entry') }}
            <span v-if="congress.title">— {{ congress.title }}</span>
        </b-card-title>

        <b-row
            align-v="center"
            class="mb-1"
        >
            <b-col
                cols="12"
                md="4"
            >
                <v-select
                    v-model="bulkWorkingGroup"
                    :options="workingGroupOptions"
                    label="title"
                    :placeholder="$t('Working Group')"
                />
            </b-col>
            <b-col
                cols="12"
                md="4"
            >
                <b-button
                    variant="primary"
                    :disabled="!bulkWorkingGroup || !selectedIds.length"
                    @click="applyBulkWorkingGroup"
                >
                    {{ $t('Apply to selected') }} ({{ selectedIds.length }})
                </b-button>
            </b-col>
        </b-row>

        <b-table
            :items="videos"
            :fields="fields"
            :busy="loading"
            small
            responsive
            show-empty
            :empty-text="$t('No matching records found')"
        >
            <template #head(selected)>
                <b-form-checkbox
                    :checked="allSelected"
                    @change="toggleSelectAll"
                />
            </template>
            <template #cell(selected)="data">
                <b-form-checkbox
                    :checked="selectedIds.includes(data.item.id)"
                    @change="toggleSelect(data.item.id)"
                />
            </template>

            <template #cell(speaker)="data">
                {{ data.item.speaker ? data.item.speaker.title : '' }}
            </template>
            <template #cell(room)="data">
                {{ data.item.room ? data.item.room.title : '' }}
            </template>

            <template #cell(workingGroup)="data">
                <v-select
                    v-model="data.item.workingGroup"
                    :options="workingGroupOptions"
                    label="title"
                    :placeholder="$t('Working Group')"
                />
            </template>

            <template #cell(vimeoId)="data">
                <b-form-input
                    v-model="data.item.vimeoId"
                    size="sm"
                />
            </template>
            <template #cell(vimeoHash)="data">
                <b-form-input
                    v-model="data.item.vimeoHash"
                    size="sm"
                />
            </template>

            <template #cell(actions)="data">
                <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="save(data.item)"
                >
                    {{ $t('Save') }}
                </b-button>
            </template>
            <template #cell(status)="data">
                <feather-icon
                    :icon="data.item.saved ? 'CheckIcon' : 'ClockIcon'"
                    :class="data.item.saved ? 'text-success' : 'text-secondary'"
                />
            </template>
        </b-table>
    </b-card>
</template>

<script>
import axios from '@/libs/axios'
import vSelect from 'vue-select'

export default {
    components: {
        vSelect,
    },
    props: {
        congressId: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            congress: {},
            videos: [],
            loading: false,
            workingGroupOptions: [],
            selectedIds: [],
            bulkWorkingGroup: null,
            fields: [
                { key: 'selected', label: '' },
                { key: 'ordering', label: this.$t('ordering'), sortable: true },
                { key: 'title', label: this.$t('Title') },
                { key: 'speaker', label: this.$t('Speaker') },
                { key: 'room', label: this.$t('Room') },
                { key: 'startDate', label: this.$t('Date') },
                { key: 'workingGroup', label: this.$t('Working Group') },
                { key: 'vimeoId', label: this.$t('Vimeo ID') },
                { key: 'vimeoHash', label: this.$t('vimeoHash') },
                { key: 'actions', label: '' },
                { key: 'status', label: '' },
            ],
        }
    },
    computed: {
        allSelected() {
            return this.videos.length > 0 && this.selectedIds.length === this.videos.length
        },
    },
    mounted() {
        this.loadCongress()
        this.loadVideos()
        this.loadWorkingGroups()
    },
    methods: {
        loadCongress() {
            this.$store.dispatch('app-common/fetchCongress', this.congressId).then(response => {
                this.congress = response.data
            })
        },
        loadWorkingGroups() {
            this.$store.dispatch('app-common/fetchWorkingGroups').then(response => {
                this.workingGroupOptions = response.data
            })
        },
        loadVideos() {
            this.loading = true
            axios
                .get('videos/search', {
                    params: { congress: this.congressId, size: 1000, sort: 'ordering,asc' },
                })
                .then(({ data }) => {
                    this.videos = data.content
                    this.selectedIds = []
                })
                .finally(() => {
                    this.loading = false
                })
        },
        toggleSelect(id) {
            const idx = this.selectedIds.indexOf(id)
            if (idx === -1) {
                this.selectedIds.push(id)
            } else {
                this.selectedIds.splice(idx, 1)
            }
        },
        toggleSelectAll(checked) {
            this.selectedIds = checked ? this.videos.map(v => v.id) : []
        },
        save(item) {
            item.saved = false
            axios
                .put('videos/quick-edit', {
                    id: item.id,
                    vimeoId: item.vimeoId,
                    vimeoHash: item.vimeoHash,
                    workingGroup: item.workingGroup,
                })
                .then(() => {
                    item.saved = true
                })
                .catch(() => {
                    this.showToast({
                        title: this.$t('Save Failure'),
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    })
                })
        },
        applyBulkWorkingGroup() {
            axios
                .put('videos/working-group/bulk', {
                    videoIds: this.selectedIds,
                    workingGroupId: this.bulkWorkingGroup.id,
                })
                .then(() => {
                    this.videos
                        .filter(v => this.selectedIds.includes(v.id))
                        .forEach(v => {
                            v.workingGroup = this.bulkWorkingGroup
                        })
                    this.showToast({
                        title: this.$t('Save Success'),
                        icon: 'CheckIcon',
                        variant: 'success',
                    })
                })
                .catch(() => {
                    this.showToast({
                        title: this.$t('Save Failure'),
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    })
                })
        },
    },
}
</script>